import { useEffect, useState, useCallback } from "react";
import { apiClient, API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";
import {
  Users, Search, Download, Loader2, Mail, Phone, MapPin, Gift, CreditCard,
  Calendar, User, Building2,
} from "lucide-react";
import { useStepUp } from "../hooks/useStepUp";
import StepUpUnlockPanel from "./StepUpUnlockPanel";

function gaBadge(on) {
  return on
    ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Gift Aid</Badge>
    : <Badge className="bg-slate-100 text-slate-500 hover:bg-slate-100">No GA</Badge>;
}
function payBadge(status) {
  if (!status) return <span className="text-xs text-slate-400">—</span>;
  const tone = status === "active" || status === "succeeded"
    ? "bg-emerald-100 text-emerald-800"
    : status.includes("pending") ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700";
  return <Badge className={`${tone} hover:${tone}`}>{status}</Badge>;
}

export default function CharityDonorCRM({ charityId }) {
  const { token: authToken } = useAuth();
  const stepUp = useStepUp(authToken);
  const [loading, setLoading] = useState(true);
  const [donors, setDonors] = useState([]);
  const [scope, setScope] = useState(null);
  const [search, setSearch] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const qs = charityId ? `?charityId=${charityId}` : "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/charity/donors${qs}`, {
        headers: stepUp.stepUpHeaders(),
      });
      setDonors(res.data?.donors || []);
      setScope(res.data?.scope || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load donors");
    } finally {
      setLoading(false);
    }
  }, [qs, stepUp]);

  // Re-load whenever the step-up state flips.
  useEffect(() => { load(); }, [load, stepUp.unlocked]);

  const openDetail = async (donor) => {
    setDetailLoading(true);
    setDetail({ loading: true });
    try {
      const key = encodeURIComponent(donor.donorKey);
      const res = await apiClient.get(`/charity/donors/${key}${qs}`, {
        headers: stepUp.stepUpHeaders(),
      });
      setDetail(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load donor detail");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const exportCsv = () => {
    const token = sessionStorage.getItem("taptrack_token");
    const url = `${API}/charity/donors/export${qs}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `charity-donors.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Donor CSV exported");
      })
      .catch(() => toast.error("Couldn't export. Please try again."));
  };

  const campaignOptions = Array.from(
    new Set(donors.map((d) => d.campaignName).filter(Boolean))
  ).sort();

  const filtered = donors.filter((d) => {
    if (campaignFilter !== "all" && (d.campaignName || "") !== campaignFilter) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return [d.name, d.email, d.phone, d.postcode, d.campaignName, d.fundraiserName]
      .some((v) => (v || "").toString().toLowerCase().includes(s));
  });

  // Per-campaign summary for the active filter (operational clarity for multi-campaign charities)
  const filteredTotal = filtered.reduce((sum, d) => sum + (Number(d.totalAmount ?? d.amount) || 0), 0);

  return (
    <div data-testid="charity-donor-crm" className="space-y-4">
      <StepUpUnlockPanel stepUp={stepUp}>
      <Card className="border-slate-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Users className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Donor Records</h2>
                <p className="text-xs text-slate-500">
                  {scope ? `${donors.length} donors across ${scope.campaignCount} campaign${scope.campaignCount === 1 ? "" : "s"}` : "Your charity's donors"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {campaignOptions.length > 0 && (
                <select
                  value={campaignFilter}
                  onChange={(e) => setCampaignFilter(e.target.value)}
                  data-testid="donor-campaign-filter"
                  className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 max-w-[200px]"
                >
                  <option value="all">All campaigns ({campaignOptions.length})</option>
                  {campaignOptions.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              )}
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
                <Input
                  placeholder="Search donors…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="donor-search-input"
                  className="pl-8 h-9 w-48"
                />
              </div>
              <Button variant="outline" size="sm" onClick={exportCsv} data-testid="donor-export-csv-btn" className="gap-1">
                <Download className="h-4 w-4" /> Export CSV
              </Button>
            </div>
          </div>

          {campaignFilter !== "all" && (
            <div className="mb-3 text-sm text-slate-600 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="donor-campaign-summary">
              <span className="font-medium">{campaignFilter}</span> · {filtered.length} donor{filtered.length === 1 ? "" : "s"} · £{filteredTotal.toLocaleString("en-GB", { maximumFractionDigits: 0 })} attributed
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-8" data-testid="donor-crm-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading donors…
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center" data-testid="donor-crm-empty">No donor records found.</p>
          ) : (
            <div className="overflow-x-auto" data-testid="donor-crm-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Contact</th>
                    <th className="py-2 pr-3">Campaign</th>
                    <th className="py-2 pr-3">Fundraiser</th>
                    <th className="py-2 pr-3">Created</th>
                    <th className="py-2 pr-3">Gift Aid</th>
                    <th className="py-2 pr-3">Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d) => (
                    <tr
                      key={d.donorKey}
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => openDetail(d)}
                      data-testid={`donor-row-${d.donorKey}`}
                    >
                      <td className="py-2.5 pr-3 font-medium text-slate-900">{d.name && d.name !== "—" ? d.name : (d.email || "(no name on record)")}</td>
                      <td className="py-2.5 pr-3 text-slate-600">
                        <div className="text-xs">{d.email || "—"}</div>
                        <div className="text-xs text-slate-400">{d.phone || ""}</div>
                      </td>
                      <td className="py-2.5 pr-3 text-slate-600">{d.campaignName || "—"}</td>
                      <td className="py-2.5 pr-3 text-slate-600">{d.fundraiserName || "—"}</td>
                      <td className="py-2.5 pr-3 text-slate-500 text-xs">{(d.createdAt || "").substring(0, 10)}</td>
                      <td className="py-2.5 pr-3">{gaBadge(d.giftAid)}</td>
                      <td className="py-2.5 pr-3">{payBadge(d.paymentStatus)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="donor-detail-dialog">
          <DialogHeader>
            <DialogTitle>Donor detail</DialogTitle>
            <DialogDescription>Read-only donor record scoped to your charity.</DialogDescription>
          </DialogHeader>
          {detailLoading || detail?.loading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : detail && detail.profile ? (
            <div className="space-y-4">
              <div>
                <div className="text-lg font-semibold text-slate-900">{detail.profile.name && detail.profile.name !== "—" ? detail.profile.name : (detail.profile.email || "(no name on record)")}</div>
                <div className="text-sm text-slate-600 space-y-0.5 mt-1">
                  <div className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-slate-400" /> {detail.profile.email || "—"}</div>
                  <div className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-slate-400" /> {detail.profile.phone || "—"}</div>
                  <div className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-slate-400" /> {detail.profile.address || "—"} {detail.profile.postcode || ""}</div>
                  <div className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-slate-400" /> Age {detail.profile.age ?? "—"} {detail.profile.dob ? `· DOB ${detail.profile.dob}` : ""}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Building2 className="h-3 w-3" /> Campaign</div>
                  <div className="font-medium text-slate-800">{detail.attribution?.campaignName || "—"}</div>
                </div>
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><User className="h-3 w-3" /> Fundraiser</div>
                  <div className="font-medium text-slate-800">{detail.attribution?.fundraiserName || "—"}</div>
                </div>
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><CreditCard className="h-3 w-3" /> Mandate / Subscription</div>
                  <div className="font-medium text-slate-800">{detail.mandateStatus || "—"} / {detail.subscriptionStatus || "—"}</div>
                </div>
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Gift className="h-3 w-3" /> Gift Aid declarations</div>
                  <div className="font-medium text-slate-800">{(detail.giftAidDeclarations || []).length}</div>
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold text-slate-700 mb-1.5">Conversion history ({(detail.conversionHistory || []).length})</div>
                <div className="space-y-1.5">
                  {(detail.conversionHistory || []).map((c) => (
                    <div key={c.id} className="flex items-center justify-between text-xs p-2 rounded border border-slate-100" data-testid="donor-conversion-row">
                      <span>{c.conversionType} · £{c.amount} {c.paymentFrequency ? `/${c.paymentFrequency}` : ""}</span>
                      <span className="text-slate-500">{(c.createdAt || "").substring(0, 10)} · {c.status}{c.giftAid ? " · GA" : ""}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold text-slate-700 mb-1.5">Payment instructions ({(detail.paymentInstructions || []).length})</div>
                <div className="space-y-1.5">
                  {(detail.paymentInstructions || []).length === 0 ? (
                    <p className="text-xs text-slate-400">No payment instructions recorded.</p>
                  ) : (detail.paymentInstructions || []).map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-xs p-2 rounded border border-slate-100" data-testid="donor-pi-row">
                      <span>{p.conversionType} · £{p.amount} · {p.credentialSource || "—"}</span>
                      <span className="text-slate-500">{p.status} · {(p.createdAt || "").substring(0, 10)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      </StepUpUnlockPanel>
    </div>
  );
}
